import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { MantineReactTable, type MRT_ColumnDef, type MRT_SortingState } from 'mantine-react-table';
import { Checkbox, Group, ActionIcon, Loader, Text, Stack } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { IconCalculator, IconEdit, IconFileText } from '@tabler/icons-react';
import CroppedFieldsMap from './CroppedFieldsMap';
import CropPickerModal from './CropPickerModal';
import { createHeaderWithHelp, useHelpPanel } from '../utils/tableHelpers';
import { API_ENDPOINTS } from '../constants/api';
import { calculateDerivedCarbonValues, formatNumber } from '../utils/carbonIntensityCalculations';
import { formatTillageForDisplay } from '../utils/tillageDisplay';
import './CarbonIntensityGrid.css';

interface CarbonIntensityGridProps {
  selectedYear: number;
  userToken: string;
  selectedOrganizationId?: string;
  fieldsWithCrops?: any[];
  isLoading?: boolean;
  onDataRefresh?: () => void;
  engagementLevel?: number;
}

interface CarbonIntensityData {
  business: string;
  farm: string;
  fieldname: string;
  crop: string;
  crop_id?: string;
  field_id: string;
  planting_id: string;
  acres: number;
  yield?: number | null;
  bushelsPerAcre: number;
  expected_yield?: number | null;
  total_synthetic_n?: number | null;
  nitrification_inhibitor_percentage?: number | null;
  tillage: string | null;
  tdisc_designation?: string | null;
  tdisc_calculated_at?: string | null;
  tdisc_pending?: boolean;
  tillage_event_count?: number;
  cover_crop_used?: string;
  planting_date?: string;
  harvest_date?: string;

  // Carbon scores
  default_ci_score: number | null;
  ci_score: number | null;
  total_ghg: number | null;
  default_total_ghg: number | null;
  fdcic_evidence_url?: string | null;

  geojson?: any;
}

export default function CarbonIntensityGrid({
  selectedYear,
  userToken,
  selectedOrganizationId,
  fieldsWithCrops = [],
  isLoading = false,
  onDataRefresh,
  engagementLevel = 1,
}: CarbonIntensityGridProps) {
  // Detect mobile screen size
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [saveMessage, setSaveMessage] = useState('');

  // Map modal state
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedFieldForMap, setSelectedFieldForMap] = useState<CarbonIntensityData | null>(null);

  // Help panel state
  const { showHelpPanel, helpContent, helpPosition, arrowDirection, handleHelpClick, closeHelpPanel } = useHelpPanel();
  const [hideEmptyColumns, setHideEmptyColumns] = useState(true);

  // Clear carbon data state
  const [isClearingData, setIsClearingData] = useState(false);

  // State for sorting persistence
  const [sorting, setSorting] = useState<MRT_SortingState>(() => {
    const saved = localStorage.getItem('carbonIntensityGridSorting');
    return saved ? JSON.parse(saved) : [{ id: 'fieldname', desc: false }];
  });

  useEffect(() => {
    localStorage.setItem('carbonIntensityGridSorting', JSON.stringify(sorting));
  }, [sorting]);

  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  // Carbon scores display state
  const [showCarbonScores, setShowCarbonScores] = useState(false);
  const [calculatingPlantingId, setCalculatingPlantingId] = useState<string | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedFieldForEdit, setSelectedFieldForEdit] = useState<CarbonIntensityData | null>(null);

  const handleEditField = (fieldData: CarbonIntensityData) => {
    setSelectedFieldForEdit(fieldData);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedFieldForEdit(null);
  };

  const handleCropUpdated = () => {
    onDataRefresh?.();
    handleCloseEditModal();
  };

  // Handle carbon calculator for single planting
  const handleCalculateCarbon = async (plantingId: string) => {
    if (!userToken || !plantingId) {
      console.error('Missing userToken or plantingId');
      return;
    }

    setCalculatingPlantingId(plantingId);
    try {
      const organizationParam = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
      const response = await fetch(`${API_ENDPOINTS.CARBON_INTENSITY}/calculate-excel-single${organizationParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({
          planting_id: plantingId,
          year: selectedYear
        })
      });

      if (response.ok) {
        const calcResult = await response.json().catch(() => null);
        if (onDataRefresh) {
          await onDataRefresh();
        }
        // If refresh still lags, surface tillage from the calculate response in the console for diagnosis
        if (calcResult?.tillage != null) {
          console.log(
            `🌾 Tillage after calculate: ${calcResult.tillage}` +
              (calcResult.tdisc_designation
                ? ` (${calcResult.tdisc_designation})`
                : ''),
          );
        }
      } else {
        const errorText = await response.text();
        console.error('🌾 Error calculating carbon intensity:', errorText);
        throw new Error(`API Error: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('🌾 Error calculating carbon intensity:', error);
    } finally {
      setCalculatingPlantingId(null);
    }
  };

  const handleDownloadEvidence = async (plantingId: string) => {
    if (!userToken || !plantingId) return;
    try {
      const organizationParam = selectedOrganizationId
        ? `?organization_id=${selectedOrganizationId}`
        : '';
      const response = await fetch(
        `${API_ENDPOINTS.CARBON_INTENSITY}/evidence/${plantingId}${organizationParam}`,
        {
          headers: { Authorization: `Bearer ${userToken}` },
        },
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fdcic-evidence_${plantingId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading FD-CIC evidence:', error);
    }
  };

  // Check if there's any carbon data in the current view
  const hasCarbonData = useMemo(() => {
    const hasOriginalInputData = fieldsWithCrops.some(field => {
      const carbonFields = [
        'total_synthetic_n',
        'nitrification_inhibitor_percentage',
        'tillage',
        'expected_yield',
        'cover_crop_used',
      ];
      return carbonFields.some(fieldKey => {
        const value = field[fieldKey as keyof typeof field];
        return value !== null && value !== undefined && value !== '' && value !== 0;
      });
    });

    // Also show Clear button when calculated score results exist,
    // even if no carbon input data was entered.
    const hasCalculatedResults = fieldsWithCrops.some(field => {
      const calculatedFields = ['ci_score', 'default_ci_score', 'total_ghg', 'default_total_ghg'];
      return calculatedFields.some(fieldKey => {
        const value = field[fieldKey as keyof typeof field];
        return value !== null && value !== undefined && value !== '';
      });
    });

    return hasOriginalInputData || hasCalculatedResults;
  }, [fieldsWithCrops]);

  // Filter only fields with crops and transform data for carbon intensity
  const rowData = useMemo(() => {
    return fieldsWithCrops
      .filter(field => field && (field.crop_id || field.crop))
      .map(field => {
        const business = field.business || field.business_unit || 'Unknown';
        const farm = field.farm || field.farm_name || 'Unknown';
        const fieldname = field.fieldname || field.field_name || field.name || 'Unknown';
        const crop = field.crop || `Crop ID: ${field.crop_id}` || 'Unknown';
        const acres = field.crop_acres
          ? parseFloat(field.crop_acres.toString())
          : (field.acres ? parseFloat(field.acres.toString()) : 0);
        const yieldValue = field.yield != null ? parseFloat(field.yield.toString()) : null;

        return {
          business,
          farm,
          fieldname,
          crop,
          crop_id: field.crop_id,
          field_id: field.field_id || field.fieldname,
          planting_id: field.planting_id,
          acres,
          yield: yieldValue,
          planting_date: field.planting_date,
          harvest_date: field.harvest_date,
          bushelsPerAcre: yieldValue != null && acres
            ? yieldValue / acres
            : 0,
          expected_yield: field.expected_yield != null
            ? parseFloat(field.expected_yield.toString())
            : null,
          total_synthetic_n: field.total_synthetic_n != null
            ? parseFloat(field.total_synthetic_n.toString())
            : null,
          nitrification_inhibitor_percentage:
            field.nitrification_inhibitor_percentage != null
              ? parseFloat(field.nitrification_inhibitor_percentage.toString())
              : null,
          tillage: field.tillage ?? null,
          tdisc_designation: field.tdisc_designation ?? null,
          tdisc_calculated_at: field.tdisc_calculated_at ?? null,
          tdisc_pending: Boolean(field.tdisc_pending),
          tillage_event_count: field.tillage_event_count ?? 0,
          cover_crop_used: field.cover_crop_used,

          // Carbon scores
          default_ci_score: field.default_ci_score != null ? parseFloat(field.default_ci_score.toString()) : null,
          ci_score: field.ci_score != null ? parseFloat(field.ci_score.toString()) : null,
          total_ghg: field.total_ghg != null ? parseFloat(field.total_ghg.toString()) : null,
          default_total_ghg: field.default_total_ghg != null ? parseFloat(field.default_total_ghg.toString()) : null,
          fdcic_evidence_url: field.fdcic_evidence_url || null,

          geojson: field.geojson,
        };
      });
  }, [fieldsWithCrops]);

  const shouldHideBusinessUnit = useMemo(() => {
    const uniqueBusinesses = new Set(rowData.map(field => field.business));
    return uniqueBusinesses.size <= 1;
  }, [rowData]);

  const shouldHideFarm = useMemo(() => {
    const uniqueFarms = new Set(rowData.map(field => field.farm));
    return uniqueFarms.size <= 1;
  }, [rowData]);

  const shouldShowColumn = useMemo(() => {
    return (columnKey: string) => {
      if (!hideEmptyColumns) return true;

      // Always show these essential columns regardless of data
      if (['actions', 'fieldname', 'crop', 'acres', 'total_synthetic_n', 'nitrification_inhibitor_percentage', 'cover_crop_used'].includes(columnKey)) {
        return true;
      }

      const hasDataInColumn = rowData.some(row => {
        const value = row[columnKey as keyof typeof row];
        return value !== null && value !== undefined && value !== '' && value !== 0;
      });

      return hasDataInColumn;
    };
  }, [hideEmptyColumns, rowData]);

  // Column definitions for carbon intensity (read-only)
  const columns: MRT_ColumnDef<CarbonIntensityData>[] = useMemo(() => {
    const allBaseColumns: MRT_ColumnDef<CarbonIntensityData>[] = [
      {
        accessorKey: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        enableResizing: false,
        Cell: ({ row }) => (
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <ActionIcon
              size="sm"
              variant="light"
              color="green"
              onClick={() => handleEditField(row.original)}
              title="Edit Crop Details"
            >
              <IconEdit size={16} />
            </ActionIcon>

            {engagementLevel > 1 && (
              <ActionIcon
                size="sm"
                variant="light"
                color="blue"
                loading={calculatingPlantingId === row.original.planting_id}
                onClick={() => handleCalculateCarbon(row.original.planting_id)}
                title="Calculate Carbon Intensity"
                disabled={!row.original.planting_id}
              >
                <IconCalculator size={16} />
              </ActionIcon>
            )}

            {row.original.fdcic_evidence_url && (
              <ActionIcon
                size="sm"
                variant="light"
                color="grape"
                onClick={() => handleDownloadEvidence(row.original.planting_id)}
                title="Calculator evidence PDF"
              >
                <IconFileText size={16} />
              </ActionIcon>
            )}

            <button
              onClick={() => {
                if (row.original.geojson) {
                  setSelectedFieldForMap(row.original);
                  setShowMapModal(true);
                }
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                color: row.original.geojson ? '#007bff' : '#ccc'
              }}
              disabled={!row.original.geojson}
              title={row.original.geojson ? `View ${row.original.fieldname} on map` : 'No map data available'}
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} />
            </button>
          </div>
        ),
      },
      {
        accessorKey: 'fieldname',
        header: 'Field',
        size: 120,
      },
      {
        accessorKey: 'crop',
        header: 'Crop',
        size: 100
      },
      {
        accessorKey: 'acres',
        header: 'Acres',
        size: 80,
        Cell: ({ cell }) => {
          const value = cell.getValue<number>();
          return value != null ? value.toFixed(1) : '';
        },
      },
      {
        accessorKey: 'total_synthetic_n',
        header: 'Total Synthetic N',
        size: 140,
        Header: ({ column }) => createHeaderWithHelp(
          column.columnDef.header as string,
          'Total synthetic nitrogen (lbs N/acre), derived from fertilizer applications.',
          handleHelpClick
        ),
        Cell: ({ cell }) => {
          const value = cell.getValue<number | null>();
          if (value === null || value === undefined) return '';
          return Number.isFinite(value) ? value.toFixed(1) : '';
        },
      },
      {
        accessorKey: 'nitrification_inhibitor_percentage',
        header: 'NI %',
        size: 90,
        Header: ({ column }) => createHeaderWithHelp(
          column.columnDef.header as string,
          'Nitrification inhibitor percentage — N-weighted average from fertilizer applications.',
          handleHelpClick
        ),
        Cell: ({ cell }) => {
          const value = cell.getValue<number | null>();
          if (value === null || value === undefined) return '';
          return Number.isFinite(value) ? value.toFixed(1) : '';
        },
      },
      {
        accessorKey: 'tillage',
        header: 'Tillage',
        size: 140,
        Header: ({ column }) => createHeaderWithHelp(
          column.columnDef.header as string,
          'Tillage method used on this field. Edit this value in Crop Details.',
          handleHelpClick
        ),
        Cell: ({ cell, row }) => {
          const tillage = cell.getValue<string>() || '';
          const designation = row.original.tdisc_designation;
          if (!tillage) return '';
          const label = formatTillageForDisplay(tillage);
          return designation ? `${label} (${designation})` : label;
        },
      },
      {
        accessorKey: 'expected_yield',
        header: 'Expected Yield',
        size: 120,
        Header: ({ column }) => createHeaderWithHelp(
          column.columnDef.header as string,
          'Expected yield (bu/acre). Edit this value in Crop Details.',
          handleHelpClick
        ),
        Cell: ({ cell }) => {
          const value = cell.getValue<number | null>();
          if (value === null || value === undefined) return '';
          return Number.isFinite(value) ? value.toFixed(1) : '';
        },
      },
      {
        accessorKey: 'cover_crop_used',
        header: 'Cover Crop',
        size: 160,
        Header: ({ column }) => createHeaderWithHelp(
          column.columnDef.header as string,
          'Cover crop practice for this planting. Edit this value in Crop Details.',
          handleHelpClick
        ),
        Cell: ({ cell }) => cell.getValue<string>() || '',
      },
    ];

    const baseColumns = allBaseColumns.filter(column => {
      if (!column.accessorKey) return true;
      return shouldShowColumn(column.accessorKey);
    });

    const finalColumns: MRT_ColumnDef<CarbonIntensityData>[] = [];

    if (!shouldHideBusinessUnit) {
      finalColumns.push({
        accessorKey: 'business',
        header: 'Business Unit',
        size: 150
      });
    }

    if (!shouldHideFarm) {
      finalColumns.push({
        accessorKey: 'farm',
        header: 'Farm',
        size: 150
      });
    }

    if (showCarbonScores) {
      finalColumns.push(
        ...(engagementLevel > 1 ? [{
          accessorKey: 'total_ghg',
          header: 'g GHG Emissions (per bushel)',
          size: 100,
          enableColumnFilter: false,
          Cell: ({ cell }: { cell: any }) => {
            if (cell.getValue() === null || cell.getValue() === undefined) return '';
            const v = Number(cell.getValue());
            return Number.isFinite(v) ? v.toFixed(1) : '0.0';
          },
        }] : []),
        {
          accessorKey: 'ghg_per_mj_fixed',
          header: 'Default Carbon Intensity Score',
          size: 100,
          enableColumnFilter: false,
          Cell: ({ row }) => {
            const hasCalculatedScores =
              row.original.ci_score != null ||
              row.original.default_ci_score != null ||
              row.original.total_ghg != null ||
              row.original.default_total_ghg != null;
            if (!hasCalculatedScores) return '';

            const derived = calculateDerivedCarbonValues(
              row.original.crop,
              row.original.ci_score,
              row.original.default_ci_score,
              row.original.total_ghg,
              row.original.default_total_ghg,
              row.original.bushelsPerAcre
            );
            return formatNumber(derived.ghgPerMjFixed, 1);
          },
        },
        ...(engagementLevel > 1 ? [{
          accessorKey: 'ci_score',
          header: 'Carbon Intensity Score',
          size: 70,
          enableColumnFilter: false,
          Cell: ({ cell }: { cell: any }) => {
            if (cell.getValue() === null || cell.getValue() === undefined) return '';
            const v = Number(cell.getValue());
            return Number.isFinite(v) ? v.toFixed(1) : '0.0';
          },
        }] : []),
        {
          accessorKey: 'kg_ghg_per_mmbtu',
          header: 'kg GHG Emissions per mmBtu',
          size: 120,
          Cell: ({ row }) => {
            const hasCalculatedScores =
              row.original.ci_score != null ||
              row.original.default_ci_score != null ||
              row.original.total_ghg != null ||
              row.original.default_total_ghg != null;
            if (!hasCalculatedScores) return '';

            const derived = calculateDerivedCarbonValues(
              row.original.crop,
              row.original.ci_score,
              row.original.default_ci_score,
              row.original.total_ghg,
              row.original.default_total_ghg,
              row.original.bushelsPerAcre
            );
            return formatNumber(derived.kgGhgPerMmbtu, 2);
          }
        },
      );
    }

    return [...finalColumns, ...baseColumns];
  }, [shouldHideBusinessUnit, shouldHideFarm, shouldShowColumn, showCarbonScores, calculatingPlantingId, engagementLevel, handleHelpClick]);

  // Function to clear all carbon data
  const handleClearCarbonData = useCallback(async () => {
    if (isClearingData) return;

    setIsClearingData(true);
    setSaveMessage('Clearing carbon data...');

    try {
      const organizationParam = selectedOrganizationId ? `?organization_id=${selectedOrganizationId}` : '';
      const url = `${API_ENDPOINTS.CLEAR_CARBON_DATA}${organizationParam}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        },
        body: JSON.stringify({ year: selectedYear })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const result = await response.json();
      console.log('✅ API Response:', result);

      if (result.success) {
        setSaveMessage(`Successfully cleared carbon data for ${result.fields_updated} fields`);

        if (onDataRefresh) {
          onDataRefresh();
        } else {
          setTimeout(() => {
            setSaveMessage('Data cleared successfully. Please refresh the page to see updated data.');
          }, 2000);
        }
      } else {
        console.error('❌ Clear operation failed:', result.error);
        setSaveMessage(`Error clearing carbon data: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('💥 Error clearing carbon data:', error);
      setSaveMessage(`Error clearing carbon data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsClearingData(false);
      setShowClearConfirmation(false);
    }
  }, [isClearingData, selectedYear, userToken, selectedOrganizationId, onDataRefresh]);

  return (
    <div className="carbon-intensity-grid">
      {calculatingPlantingId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '40px 50px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              textAlign: 'center',
              minWidth: '320px',
            }}
          >
            <Stack align="center" spacing="md">
              <Loader size="xl" />
              <Text size="lg" fw={600}>Calculating Carbon Intensity</Text>
              <Text size="sm" c="dimmed">Please wait while we process your data...</Text>
            </Stack>
          </div>
        </div>
      )}
      <div className="carbon-intensity-header">
        <h3>Carbon Intensity Information for Year {selectedYear}</h3>
        <div className="header-actions">
          <Group spacing="xs" style={{ alignItems: 'center' }}>
            <Checkbox
              checked={showCarbonScores}
              onChange={(event) => setShowCarbonScores(event.currentTarget.checked)}
              label="Show Carbon Scores"
              size="sm"
            />
          </Group>

          {hasCarbonData && (
            <button
              className="clear-carbon-data-btn"
              onClick={() => setShowClearConfirmation(true)}
              disabled={isClearingData}
            >
              {isClearingData ? 'Clearing...' : 'Clear All Carbon Data'}
            </button>
          )}

          <button
            className="column-toggle-btn"
            onClick={() => setHideEmptyColumns(!hideEmptyColumns)}
            title={hideEmptyColumns ? "Show all columns" : "Hide empty columns"}
          >
            {hideEmptyColumns ? "👁️ Show All Columns" : "🙈 Hide Empty Columns"}
          </button>

          {saveMessage && (
            <span className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
              {saveMessage}
            </span>
          )}
        </div>
      </div>

      <div className="carbon-intensity-content">
        {isLoading ? (
          <div className="carbon-intensity-loading">
            <div className="loading-content">
              <h3>Loading Carbon Intensity Data...</h3>
              <p>Please wait while we load the data for {selectedYear}.</p>
            </div>
          </div>
        ) : rowData.length === 0 ? (
          <div className="carbon-intensity-no-data">
            <h3>No Carbon Intensity Data</h3>
            <p>No carbon intensity data available for {selectedYear}.</p>
            <p>Edit crop details on each field to enter Total Synthetic N, tillage, and expected yield.</p>
          </div>
        ) : (
          <>
            <MantineReactTable
              key={`carbon-grid-${selectedYear}`}
              columns={columns}
              data={rowData}
              enableGrouping={true}
              enableColumnFilters={true}
              enableSorting={true}
              enableRowSelection={false}
              enableColumnResizing={true}
              enablePagination={false}
              enableColumnActions={false}
              enableTopToolbar={false}
              enableBottomToolbar={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enableColumnDragging={false}
              enableStickyHeader={!isMobile}
              enablePinning={!isMobile}
              state={{
                density: 'xs',
                columnPinning: isMobile ? { left: [] } : {
                  left: [
                    'actions',
                    ...(shouldHideBusinessUnit ? [] : ['business']),
                    ...(shouldHideFarm ? [] : ['farm']),
                    'fieldname',
                    'crop',
                    ...(showCarbonScores ? ['total_ghg', 'ghg_per_mj_fixed', 'ci_score', 'kg_ghg_per_mmbtu'] : [])
                  ],
                },
                expanded: true,
                sorting: sorting,
              }}
              onSortingChange={setSorting}
              mantineTableProps={{
                striped: false,
                highlightOnHover: true,
                withBorder: true,
                withColumnBorders: true,
                horizontalSpacing: 'md',
                style: {
                  '--mantine-table-row-height': '24px',
                  '--mantine-table-header-height': 'auto',
                } as React.CSSProperties,
              }}
              mantinePaperProps={{
                shadow: 'md',
                radius: 'md',
                style: {
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                  height: 'calc(100vh - 333px)',
                  overflow: 'hidden',
                },
              }}
              mantineTableContainerProps={{
                style: {
                  height: '100%',
                  overflow: 'auto',
                  maxWidth: '100%',
                  minWidth: isMobile ? '100%' : '1200px',
                }
              }}
            />
          </>
        )}
      </div>

      {/* Map Modal */}
      {showMapModal && selectedFieldForMap && (
        <div className="map-modal-overlay" onClick={() => setShowMapModal(false)}>
          <div className="map-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="map-modal-header">
              <h3>Field Map: {selectedFieldForMap.fieldname}</h3>
              <button
                className="map-modal-close"
                onClick={() => setShowMapModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="map-modal-body">
              <div className="field-info">
                <p><strong>Field:</strong> {selectedFieldForMap.fieldname}</p>
                <p><strong>Farm:</strong> {selectedFieldForMap.farm}</p>
                <p><strong>Crop:</strong> {selectedFieldForMap.crop}</p>
                <p><strong>Acres:</strong> {selectedFieldForMap.acres}</p>
              </div>
              <div className="carbon-intensity-grid-field-map-container">
                <CroppedFieldsMap
                  fields={[{
                    field_id: selectedFieldForMap.fieldname,
                    fieldname: selectedFieldForMap.fieldname,
                    farm_id: selectedFieldForMap.farm,
                    acres: selectedFieldForMap.acres,
                    geojson: selectedFieldForMap.geojson,
                    state: '',
                    county: '',
                    crop: selectedFieldForMap.crop,
                    business: selectedFieldForMap.business,
                    farm: selectedFieldForMap.farm
                  }]}
                  selectedFieldId={selectedFieldForMap.fieldname}
                  mapStyle="satellite"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help Panel */}
      {showHelpPanel && (
        <div
          className="help-panel-overlay"
          onClick={closeHelpPanel}
        >
          <div
            className="help-panel"
            style={{
              position: 'fixed',
              left: helpPosition.x,
              top: helpPosition.y,
              zIndex: 1000
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="help-panel-header">
              <span>Help</span>
              <button
                className="help-panel-close"
                onClick={closeHelpPanel}
              >
                ✕
              </button>
            </div>
            <div className="help-panel-content">
              {helpContent}
            </div>
            <div className={`help-panel-arrow ${arrowDirection === 'left' ? 'help-panel-arrow-left' : ''}`}></div>
          </div>
        </div>
      )}

      {/* Clear Carbon Data Confirmation Modal */}
      {showClearConfirmation && (
        <div className="clear-confirmation-overlay" onClick={() => setShowClearConfirmation(false)}>
          <div className="clear-confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="clear-confirmation-header">
              <h3>Clear All Carbon Data</h3>
              <button
                className="clear-confirmation-close"
                onClick={() => setShowClearConfirmation(false)}
              >
                ✕
              </button>
            </div>
            <div className="clear-confirmation-body">
              <p>
                <strong>Warning:</strong> This action will permanently delete all carbon intensity data for {selectedYear}, including:
              </p>
              <ul>
                <li>Derived total synthetic nitrogen values</li>
                <li>Tillage method information</li>
                <li>Expected yield values used for carbon calculations</li>
                <li>Calculated carbon intensity scores (CI Score, Default CI Score, Total GHG)</li>
              </ul>
              <p>
                You can re-enter crop details and recalculate scores afterward. Are you sure you want to proceed?
              </p>
            </div>
            <div className="clear-confirmation-actions">
              <button
                className="clear-confirmation-cancel"
                onClick={() => setShowClearConfirmation(false)}
                disabled={isClearingData}
              >
                Cancel
              </button>
              <button
                className="clear-confirmation-confirm"
                onClick={handleClearCarbonData}
                disabled={isClearingData}
              >
                {isClearingData ? 'Clearing...' : 'Clear All Data'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Field Modal */}
      {editModalOpen && selectedFieldForEdit && userToken && selectedFieldForEdit.field_id && (
        <CropPickerModal
          isOpen={editModalOpen}
          onClose={handleCloseEditModal}
          fieldId={selectedFieldForEdit.field_id}
          fieldName={selectedFieldForEdit.fieldname}
          fieldAcres={selectedFieldForEdit.acres}
          year={selectedYear}
          userToken={userToken}
          onCropAdded={handleCropUpdated}
          mode="edit"
          selectedOrganizationId={selectedOrganizationId}
          engagementLevel={engagementLevel}
          readonlyCropType={true}
          existingCropData={{
            planting_id: selectedFieldForEdit.planting_id,
            crop_id: selectedFieldForEdit.crop_id || '',
            acres: selectedFieldForEdit.acres.toString(),
            planting_date: selectedFieldForEdit.planting_date || '',
            harvest_date: selectedFieldForEdit.harvest_date || '',
            yield: selectedFieldForEdit.yield ?? undefined,
            bushelsPerAcre: selectedFieldForEdit.bushelsPerAcre || 0,
            expected_yield: selectedFieldForEdit.expected_yield,
            total_synthetic_n: selectedFieldForEdit.total_synthetic_n,
            tillage: selectedFieldForEdit.tillage,
            tdisc_designation: selectedFieldForEdit.tdisc_designation,
            tdisc_calculated_at: selectedFieldForEdit.tdisc_calculated_at,
            tdisc_pending: selectedFieldForEdit.tdisc_pending,
            tillage_event_count: selectedFieldForEdit.tillage_event_count,
            cover_crop_used: selectedFieldForEdit.cover_crop_used || 'No Cover Crop',
          }}
        />
      )}
    </div>
  );
}
